import { supabase } from './supabaseClient';
import { addIncome, deleteIncome } from './finances';
import { todayISO } from './dates';

// Consignment: stock placed with a shop, paid for only as it sells.
//
// The rule everything here protects: **units sitting in someone else's shop
// are inventory, not revenue and not a receivable.** Money exists only where
// a month's sales have been reported, and it reaches Accounting only when
// that month's statement is actually paid.
//
// Every figure below is derived from the statements and the lines they were
// reported against. Nothing about sell-through is stored, so a badge or a
// total can never disagree with the rows underneath it.

export const STATEMENT_STATUSES = ['Awaiting payment', 'Paid'];

// The four statuses the user asked for, deliberately no more: this is a
// small business, not a receivables department.
export const STOCKIST_STATUSES = [
  'Active',
  'Awaiting monthly sales',
  'Payment due',
  'Completed',
  'Cancelled',
];

export const STOCKIST_STATUS_TONE = {
  Active: 'accent',
  'Awaiting monthly sales': 'neutral',
  'Payment due': 'warning',
  Completed: 'success',
  Cancelled: 'muted',
};

// ---------------------------------------------------------------------------
// Months
// ---------------------------------------------------------------------------

// Months are stored as the first day of the month, as a local date string.
// Built from parts rather than via toISOString(), for the same reason
// todayISO() exists — UTC would roll a Jamaican evening into the next month.
export function monthKey(value) {
  const d = value instanceof Date ? value : parseMonth(value);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function parseMonth(value) {
  const [y, m] = String(value).slice(0, 10).split('-').map(Number);
  return new Date(y, (m || 1) - 1, 1);
}

export function formatMonth(value) {
  return parseMonth(value).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

// The months worth offering when recording sales: the current month back
// through the month the stock was sent, newest first, minus any already
// recorded. Offering a list beats a free date field — you report the month
// that just ended, and typing a date is how you file August's sales under
// 2025.
export function availableMonths(invoice, statements, today = todayISO()) {
  const start = parseMonth(monthKey(invoice.issue_date));
  const end = parseMonth(monthKey(today));
  const taken = new Set((statements || []).map((s) => monthKey(s.period_month)));

  const months = [];
  const cursor = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cursor >= start) {
    const key = monthKey(cursor);
    if (!taken.has(key)) months.push(key);
    cursor.setMonth(cursor.getMonth() - 1);
  }
  return months;
}

// ---------------------------------------------------------------------------
// Sell-through
// ---------------------------------------------------------------------------

// One row per product on the invoice, answering "what happened to what I
// sent?" without anyone doing arithmetic:
//
//   sent · soldToDate · soldThisMonth · returned · remaining · value
//
// soldToDate counts every statement, paid or not — they sold it either way;
// payment is a separate question. `forMonth` is what lets the same function
// power the sell-through table and the "sold this month" column beside it.
export function sellThrough(invoice, statements, { forMonth = null } = {}) {
  const items = invoice?.invoice_items || [];
  const monthTarget = forMonth ? monthKey(forMonth) : null;

  return items.map((item) => {
    let soldToDate = 0;
    let soldThisMonth = 0;

    for (const statement of statements || []) {
      for (const line of statement.invoice_statement_lines || []) {
        if (line.invoice_item_id !== item.id) continue;
        const qty = Number(line.quantity_sold) || 0;
        soldToDate += qty;
        if (monthTarget && monthKey(statement.period_month) === monthTarget) {
          soldThisMonth += qty;
        }
      }
    }

    const sent = Number(item.quantity) || 0;
    const returned = Number(item.quantity_returned) || 0;
    const unitPrice = Number(item.unit_price) || 0;
    // Clamped: a correction that over-reports sales shouldn't render a
    // negative number of bars sitting on a shelf.
    const remaining = Math.max(0, sent - soldToDate - returned);

    return {
      itemId: item.id,
      productId: item.product_id,
      description: item.description,
      sent,
      soldToDate,
      soldThisMonth,
      returned,
      remaining,
      unitPrice,
      soldValue: soldToDate * unitPrice,
      remainingValue: remaining * unitPrice,
    };
  });
}

export function totalsOf(rows) {
  return (rows || []).reduce(
    (acc, row) => ({
      sent: acc.sent + row.sent,
      soldToDate: acc.soldToDate + row.soldToDate,
      returned: acc.returned + row.returned,
      remaining: acc.remaining + row.remaining,
      soldValue: acc.soldValue + row.soldValue,
      remainingValue: acc.remainingValue + row.remainingValue,
    }),
    { sent: 0, soldToDate: 0, returned: 0, remaining: 0, soldValue: 0, remainingValue: 0 }
  );
}

export function statementTotal(statement) {
  return (statement?.invoice_statement_lines || []).reduce(
    (sum, line) => sum + (Number(line.quantity_sold) || 0) * (Number(line.unit_price) || 0),
    0
  );
}

export function statementUnits(statement) {
  return (statement?.invoice_statement_lines || []).reduce(
    (sum, line) => sum + (Number(line.quantity_sold) || 0),
    0
  );
}

// Sold but not yet paid for — the only figure on this page that is genuinely
// money owed. Unsold stock is not in it, which is the entire reason the
// summary card is called "Payment due from sold stock" and not "Outstanding".
export function unpaidDue(statements) {
  return (statements || [])
    .filter((s) => s.status !== 'Paid')
    .reduce((sum, s) => sum + statementTotal(s), 0);
}

// ---------------------------------------------------------------------------
// Status, derived
// ---------------------------------------------------------------------------

// The parent's status, computed from its children and its remaining units.
//
// Completion is the interesting one: an invoice is finished only when every
// unit is resolved — sold or returned — **and** nothing sold is still unpaid.
// Either alone is not enough. Stock all gone with an unpaid August statement
// is still owed money; everything paid with six bars still on the shelf is
// still an open consignment.
export function statusOfStockist(invoice, statements, today = todayISO()) {
  if (invoice.cancelled_at) return 'Cancelled';

  const rows = sellThrough(invoice, statements);
  const { remaining } = totalsOf(rows);
  const due = unpaidDue(statements);

  if (remaining === 0 && due === 0) return 'Completed';
  if (due > 0) return 'Payment due';

  // Stock is still out and nothing is owed. If last month hasn't been
  // reported yet, that's the thing waiting to happen.
  const lastMonth = previousMonthKey(today);
  const reported = new Set((statements || []).map((s) => monthKey(s.period_month)));
  const startedBefore = monthKey(invoice.issue_date) <= lastMonth;
  if (startedBefore && !reported.has(lastMonth)) return 'Awaiting monthly sales';

  return 'Active';
}

function previousMonthKey(today) {
  const d = parseMonth(monthKey(today));
  d.setMonth(d.getMonth() - 1);
  return monthKey(d);
}

// What still has to happen before an invoice can close, in words. Returned by
// the close-out step so it explains itself rather than just refusing.
export function completionBlockers(invoice, statements) {
  const rows = sellThrough(invoice, statements);
  const { remaining } = totalsOf(rows);
  const due = unpaidDue(statements);

  const blockers = [];
  if (remaining > 0) {
    blockers.push(
      `${remaining} ${remaining === 1 ? 'unit is' : 'units are'} still unaccounted for — record them as sold or returned.`
    );
  }
  if (due > 0) blockers.push('There are unpaid monthly statements.');
  return blockers;
}

// The income entry a *statement* becomes. One month's sales, not the whole
// consignment — the distinction this entire module exists to preserve.
export function statementToIncomeEntry(invoice, statement, { paidOn, method, reference } = {}) {
  return {
    entry_date: paidOn || todayISO(),
    description: `${invoice.invoice_number} — ${invoice.bill_to_name} — ${formatMonth(
      statement.period_month
    )} sales`,
    currency: invoice.currency || 'JMD',
    amount: statementTotal(statement),
    exchange_rate: Number(invoice.exchange_rate) || 1,
    channel: invoice.channel || 'Wholesale',
    // Scoped to the month so two statements on one invoice can't collide, and
    // so supplying it stops the 0004 trigger minting an unrelated WH- number.
    order_number: `${invoice.invoice_number}-${monthKey(statement.period_month).slice(0, 7)}`,
    payment_method: method || statement.payment_method || null,
    notes: reference ? `Payment reference: ${reference}` : null,
    created_by: invoice.created_by || null,
  };
}

// ---------------------------------------------------------------------------
// Stockist profiles
// ---------------------------------------------------------------------------

export async function listStockists() {
  const { data, error } = await supabase.from('stockists').select('*').order('name');
  if (error) throw error;
  return data;
}

export async function addStockist(fields) {
  const { data, error } = await supabase.from('stockists').insert(fields).select().single();
  if (error) throw error;
  return data;
}

export async function updateStockist(id, updates) {
  const { data, error } = await supabase
    .from('stockists')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// Everything worth knowing about a shop the moment you pick it, derived from
// their invoices rather than stored: how many consignments, what they're
// holding now, what they've sold you in total, and what they still owe.
export function stockistSummary(stockistId, invoices, statementsByInvoice) {
  const theirs = (invoices || []).filter((inv) => inv.stockist_id === stockistId);

  let stockHeld = 0;
  let stockValue = 0;
  let totalSales = 0;
  let outstanding = 0;
  let openCount = 0;

  for (const invoice of theirs) {
    const statements = statementsByInvoice[invoice.id] || [];
    const rows = sellThrough(invoice, statements);
    const totals = totalsOf(rows);
    stockHeld += totals.remaining;
    stockValue += totals.remainingValue;
    totalSales += totals.soldValue;
    outstanding += unpaidDue(statements);
    if (statusOfStockist(invoice, statements) !== 'Completed') openCount += 1;
  }

  return {
    invoiceCount: theirs.length,
    openCount,
    stockHeld,
    stockValue,
    totalSales,
    outstanding,
    invoices: theirs,
  };
}

// ---------------------------------------------------------------------------
// Statements
// ---------------------------------------------------------------------------

export async function listStatements(invoiceIds) {
  if (!invoiceIds?.length) return {};
  const { data, error } = await supabase
    .from('invoice_statements')
    .select('*, invoice_statement_lines(*)')
    .in('invoice_id', invoiceIds)
    .order('period_month', { ascending: false });
  if (error) throw error;

  const byInvoice = {};
  for (const statement of data || []) {
    (byInvoice[statement.invoice_id] ||= []).push(statement);
  }
  return byInvoice;
}

// Records (or corrects) one month's sales. Upserted by month rather than
// appended: August is reported once, and fixing a miscount edits that month
// instead of leaving two Augusts that have to be added together.
export async function recordMonthlySales(invoiceId, periodMonth, lines, userId) {
  const month = monthKey(periodMonth);

  const { data: existing } = await supabase
    .from('invoice_statements')
    .select('id, status')
    .eq('invoice_id', invoiceId)
    .eq('period_month', month)
    .maybeSingle();

  // A paid month is settled history. Rewriting its figures would silently
  // disagree with the income entry already in the books.
  if (existing?.status === 'Paid') {
    throw new Error(
      `${formatMonth(month)} has already been paid. Un-pay that statement first if the figures need correcting.`
    );
  }

  let statementId = existing?.id;
  if (!statementId) {
    const { data, error } = await supabase
      .from('invoice_statements')
      .insert({ invoice_id: invoiceId, period_month: month, created_by: userId })
      .select()
      .single();
    if (error) throw error;
    statementId = data.id;
  }

  const { error: delError } = await supabase
    .from('invoice_statement_lines')
    .delete()
    .eq('statement_id', statementId);
  if (delError) throw delError;

  const rows = lines
    .filter((line) => (Number(line.quantity_sold) || 0) > 0)
    .map((line) => ({
      statement_id: statementId,
      invoice_item_id: line.invoice_item_id,
      quantity_sold: Number(line.quantity_sold),
      unit_price: Number(line.unit_price) || 0,
    }));

  if (rows.length) {
    const { error } = await supabase.from('invoice_statement_lines').insert(rows);
    if (error) throw error;
  }
  return statementId;
}

export async function deleteStatement(id) {
  const { error } = await supabase.from('invoice_statements').delete().eq('id', id);
  if (error) throw error;
}

// Marks one month's statement paid, and books exactly that month's money.
//
// The parent invoice is untouched — it stays open while stock is still out,
// which is the whole distinction between this and a direct invoice.
export async function markStatementPaid(invoice, statement, { paidOn, method, reference }) {
  const updates = {
    status: 'Paid',
    paid_on: paidOn || todayISO(),
    payment_method: method || null,
    reference: reference || null,
  };

  let entry = null;
  // Only when there isn't one: this is what makes paying twice safe.
  if (!statement.income_entry_id) {
    entry = await addIncome(statementToIncomeEntry(invoice, statement, { paidOn, method, reference }));
    updates.income_entry_id = entry.id;
  }

  const { error } = await supabase
    .from('invoice_statements')
    .update(updates)
    .eq('id', statement.id);
  if (error) {
    throw new Error(
      entry
        ? `The payment was recorded in Accounting (entry ${entry.id}) but the statement could not be marked paid: ${error.message}. Check Income before marking it paid again, or it will be counted twice.`
        : `Couldn't update the statement: ${error.message}`
    );
  }
  return entry;
}

// Reverses the above: removes the income entry it created and reopens the
// month. Income sitting in the books for a payment that didn't happen is a
// worse error than the reversal.
export async function unmarkStatementPaid(statement) {
  if (statement.income_entry_id) await deleteIncome(statement.income_entry_id);

  const { error } = await supabase
    .from('invoice_statements')
    .update({
      status: 'Awaiting payment',
      paid_on: null,
      income_entry_id: null,
    })
    .eq('id', statement.id);
  if (error) throw new Error(`Couldn't update the statement: ${error.message}`);
}

// ---------------------------------------------------------------------------
// Editing a consignment
// ---------------------------------------------------------------------------

// What each existing line may be changed to.
//
// A line that has sales recorded against it cannot be deleted and cannot drop
// below what has already sold or come back — those units are accounted for,
// and letting the invoice claim fewer were sent than were sold would make the
// sell-through arithmetic nonsense.
export function editableLines(invoice, statements) {
  return sellThrough(invoice, statements).map((row) => ({
    id: row.itemId,
    productId: row.productId,
    description: row.description,
    quantity: row.sent,
    unitPrice: row.unitPrice,
    // The floor: units already spoken for.
    minQuantity: row.soldToDate + row.returned,
    // Deleting a line with sales would cascade-delete the statement lines
    // pointing at it, silently erasing months you've already recorded — and
    // possibly already been paid for.
    locked: row.soldToDate > 0 || row.returned > 0,
  }));
}

// How your own stock must move when the quantities sent are corrected.
//
// Positive means units come back to your shelf (you sent fewer than recorded);
// negative means more went out. Netted per product first, because the same
// product can appear on two lines and two adjustments computed from the same
// starting count would each overwrite the other.
export function sentDeltas(existingItems, nextLines) {
  const byProduct = new Map();

  const add = (productId, amount) => {
    if (!productId || !amount) return;
    byProduct.set(productId, (byProduct.get(productId) || 0) + amount);
  };

  for (const item of existingItems || []) {
    const next = (nextLines || []).find((line) => line.id === item.id);
    const was = Number(item.quantity) || 0;
    // A removed line returns everything it sent.
    const now = next ? Number(next.quantity) || 0 : 0;
    add(item.product_id, was - now);
  }

  // Lines added during the edit send new stock out.
  for (const line of nextLines || []) {
    if (line.id) continue;
    add(line.product_id, -(Number(line.quantity) || 0));
  }

  return [...byProduct.entries()]
    .map(([productId, quantity]) => ({ productId, quantity }))
    .filter((d) => d.quantity !== 0);
}

// Saves an edited consignment **without** deleting and re-inserting its lines.
//
// This is the whole reason stockist invoices don't reuse the direct invoice's
// save path: that one replaces `invoice_items` wholesale, and every statement
// line points at an invoice item with `on delete cascade`. Replacing the lines
// would take every month of recorded sales down with them.
export async function updateStockistInvoice(invoiceId, fields, lines) {
  const { error } = await supabase.from('invoices').update(fields).eq('id', invoiceId);
  if (error) throw error;

  const { data: current, error: readError } = await supabase
    .from('invoice_items')
    .select('id')
    .eq('invoice_id', invoiceId);
  if (readError) throw readError;

  const keptIds = new Set(lines.filter((l) => l.id).map((l) => l.id));

  // Removed lines first, so a line being dropped can't collide with a new one.
  const removed = (current || []).filter((row) => !keptIds.has(row.id));
  if (removed.length) {
    const { error: delError } = await supabase
      .from('invoice_items')
      .delete()
      .in('id', removed.map((r) => r.id));
    if (delError) throw delError;
  }

  for (const [index, line] of lines.entries()) {
    const payload = {
      sort_order: index,
      description: line.description.trim(),
      product_id: line.product_id || null,
      unit_price: Number(line.unit_price) || 0,
      quantity: Number(line.quantity) || 0,
    };

    if (line.id) {
      // Updated in place, so the id every statement line references survives.
      const { error: upError } = await supabase
        .from('invoice_items')
        .update(payload)
        .eq('id', line.id);
      if (upError) throw upError;
    } else {
      const { error: insError } = await supabase
        .from('invoice_items')
        .insert({ ...payload, invoice_id: invoiceId });
      if (insError) throw insError;
    }
  }
}

// Records units handed back. Returns are per invoice line, and they put the
// stock back on your own shelf because it physically came home.
export async function recordReturns(returnsByItemId) {
  for (const [itemId, quantity] of Object.entries(returnsByItemId)) {
    const { error } = await supabase
      .from('invoice_items')
      .update({ quantity_returned: Number(quantity) || 0 })
      .eq('id', itemId);
    if (error) throw error;
  }
}

export async function cancelStockistInvoice(id) {
  const { error } = await supabase
    .from('invoices')
    .update({ cancelled_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}
